/**
 * 존 폴리곤끼리의 겹침 판정 (순수 함수, JointJS 비의존).
 *
 * 구역 소속의 정답은 언제나 `memberIds` 이고 폴리곤은 시각 표현일 뿐이라
 * (capacityZonePlanner 헤더 주석 참고), 겹침이 기능을 깨뜨리지는 않는다.
 * 그래서 **막지 않고 경고만** 한다 — 도면에 따라 일부러 겹쳐 그려야 하는
 * 경우가 있다.
 *
 * 판정은 두 갈래다:
 *  1. 변끼리 교차 — 서로 파고든 경우
 *  2. 한쪽 정점이 다른 쪽 내부 — 한 폴리곤이 다른 쪽에 통째로 들어가면
 *     변이 하나도 교차하지 않으므로 1번만으로는 못 잡는다
 *
 * **맞닿기만 한 경우는 겹침이 아니다.** 구역을 빈틈없이 이어 그리는 게
 * 오히려 정상 사용법이라, 공유 경계를 겹침으로 경고하면 경고가 전부
 * 소음이 되어 무시당한다. 그래서 교차는 강한 부등호(proper intersection)로
 * 보고, 내부 판정도 경계에서 EDGE_EPSILON 이상 떨어진 점만 인정한다.
 */

import { pointInPolygon, type XYPoint } from './zoneCctvRelations'

/**
 * 경계로부터 이 거리 안에 있는 점은 "안"으로 치지 않는다. 에디터 좌표계는
 * 주차면 한 칸이 수백 단위라 0.5 는 사람이 같은 선을 그리려 한 오차 수준이다.
 */
export const EDGE_EPSILON = 0.5

function cross(o: XYPoint, a: XYPoint, b: XYPoint): number {
  return (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x)
}

/**
 * 두 선분이 서로를 가로지르는지. 끝점이 닿기만 하거나 일직선으로 겹친
 * 경우는 false — 위 주석의 "맞닿음은 겹침 아님" 규칙을 여기서 만든다.
 */
export function segmentsProperlyIntersect(
  p1: XYPoint,
  p2: XYPoint,
  p3: XYPoint,
  p4: XYPoint,
): boolean {
  const d1 = cross(p3, p4, p1)
  const d2 = cross(p3, p4, p2)
  const d3 = cross(p1, p2, p3)
  const d4 = cross(p1, p2, p4)
  return (
    ((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) &&
    ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))
  )
}

/** 점에서 선분까지의 최단 거리. */
function distToSegment(p: XYPoint, a: XYPoint, b: XYPoint): number {
  const dx = b.x - a.x
  const dy = b.y - a.y
  const lenSq = dx * dx + dy * dy
  if (lenSq === 0) return Math.hypot(p.x - a.x, p.y - a.y)
  let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / lenSq
  t = Math.max(0, Math.min(1, t))
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy))
}

/** 점에서 폴리곤 경계(모든 변)까지의 최단 거리. */
export function distanceToBoundary(p: XYPoint, polygon: XYPoint[]): number {
  let min = Infinity
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const d = distToSegment(p, polygon[j], polygon[i])
    if (d < min) min = d
  }
  return min
}

/**
 * 경계에 얹히지 않고 확실히 안쪽인 점. ray-casting 은 경계 위에서
 * 불안정하므로(zoneCctvRelations.pointInPolygon 주석) 거리로 한 번 더 거른다.
 */
export function pointStrictlyInside(p: XYPoint, polygon: XYPoint[]): boolean {
  if (polygon.length < 3) return false
  if (distanceToBoundary(p, polygon) <= EDGE_EPSILON) return false
  return pointInPolygon(p, polygon)
}

/** 두 폴리곤이 면적을 공유하는지. 맞닿기만 하면 false. */
export function polygonsOverlap(a: XYPoint[], b: XYPoint[]): boolean {
  if (a.length < 3 || b.length < 3) return false

  for (let i = 0, ai = a.length - 1; i < a.length; ai = i++) {
    for (let j = 0, bj = b.length - 1; j < b.length; bj = j++) {
      if (segmentsProperlyIntersect(a[ai], a[i], b[bj], b[j])) return true
    }
  }

  // 한쪽이 다른 쪽에 통째로 들어간 경우 — 변 교차가 하나도 없다.
  if (a.some((p) => pointStrictlyInside(p, b))) return true
  if (b.some((p) => pointStrictlyInside(p, a))) return true
  return false
}
